import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  OnDestroy,
  isDevMode,
  Inject,
  ViewEncapsulation,
} from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { fromEvent, Subject, interval, Observable, Subscription, of } from 'rxjs';
import { catchError, finalize, sampleTime, take, takeUntil } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  UserContextHttpService,
  ServiceAgreementHttpService,
  Serviceagreementpartialitem,
  PostUserContextRequestParams,
} from '@backbase/data-ang/accesscontrol';
import { CustomSessionTimeoutService } from './session-timeout.service';

const STARTING_REMAINING_SECONDS = 60;

interface AuthResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token: string;
  'not-before-policy': number;
  session_state: string;
  scope: string;
}

interface BBAuth {
  accessExpiry: number;
  timeSkew: number;
  refreshToken: string;
}

@Component({
  selector: 'bb-session-timeout-modal',
  templateUrl: './session-timeout.component.html',
  styleUrls: ['./bn-session-timeout-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [CustomSessionTimeoutService]
})
export class CustomSessionTimeoutComponent implements OnInit, OnDestroy {
  @Input() inactivityModalTime = 60;
  destroy$ = new Subject<boolean>();
  counter$?: Subscription;
  bbAuth?: BBAuth;
  stopTimer$ = new Subject<boolean>();
  mouseMoveEvent = fromEvent(document, 'mousemove');
  isOpen = false;
  timeRemaining = STARTING_REMAINING_SECONDS;
  tick = interval(1000);
  isRefreshingToken = false;
  authorization!: string;
  serviceAgreementId?: string;

  readonly modalOptions: NgbModalOptions = {
    keyboard: false,
    backdrop: 'static',
    centered: true,
    windowClass: 'bn-session-timeout-modal',
  };

  readonly currentContext: Observable<
    Serviceagreementpartialitem | undefined
  > = this.serviceAgreementService.getServiceAgreementContext().pipe(catchError(() => of(undefined)));

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly sessionTimeoutService: CustomSessionTimeoutService,
    private readonly httpClient: HttpClient,
    private readonly serviceAgreementService: ServiceAgreementHttpService,
    private readonly userContextService: UserContextHttpService,
  ) {}

  ngOnInit(): void {
    const bbAuth = sessionStorage.getItem('BB-Auth');
    this.bbAuth = bbAuth ? JSON.parse(bbAuth) : '';
    this.currentContext.pipe(take(1)).subscribe((context: Serviceagreementpartialitem | undefined): void => {
      this.serviceAgreementId = context?.id;
    });

    if (!isDevMode()) {
      this.mouseMoveEvent.pipe(takeUntil(this.destroy$), sampleTime(600)).subscribe((e: Event): void => {
        if (!this.isOpen && this.timeToLive() < 200) {
          this.refresh();
        }
      });

      this.initCounterSubscription();
    }
  }

  initCounterSubscription() {
    this.authorization = this.getCookie('Authorization');
    this.counter$ = this.tick.subscribe((n: number): void => {
      if (!this.getCookie('Authorization')) {
        if (location.href.indexOf('service-agreements') !== -1) {
          this.logout();
        } else {
          this.refreshUserContext();
        }
      }

      if (this.timeToLive() < this.inactivityModalTime) {
        this.openModal();
      }
    });
  }

  timeToLive(): number {
    if (this.authorization) {
      const jwt = this.parseJwt(this.authorization);
      const currentTime = Math.floor(Date.now() / 1000);

      if (jwt?.exp) {
        return jwt.exp - currentTime;
      }
    }
    return 0;
  }

  async logout(): Promise<void> {
    this.closeModal();
    this.counter$?.unsubscribe();
    // await this.logoutService.logout();
    // this.logoutService.goToLoginPage();
    return this.sessionTimeoutService.logout().then(() => this.goToLoginPage());
  }

  private goToLoginPage() {
    this.sessionTimeoutService.goToLoginPage();
  }

  private openModal(): void {
    const timeToLive = this.timeToLive();
    this.timeRemaining = timeToLive;

    if (timeToLive < 1) {
      this.logout();
      return;
    }

    if (!this.isOpen) {
      this.isOpen = true;
    }
    this.cd.detectChanges();
  }

  private closeModal(): void {
    this.isOpen = false;
    this.cd.detectChanges();
  }

  refresh(): void {
    this.sessionTimeoutService.refresh();
    this.closeModal();
    this.initCounterSubscription();
  }

  parseJwt(token: string) {
    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(function (c: string): string {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );
      return JSON.parse(jsonPayload);
    }
    return null;
  }

  getCookie(name: string): string {
    const ca: Array<string> = document.cookie.split(';');
    const caLen: number = ca.length;
    const cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }

  refreshUserContext(): void {
    if (this.serviceAgreementId) {
      document.cookie = `Authorization=${this.authorization}; path=/`;
      if (this.bbAuth) {
        localStorage.setItem('BB-Auth', JSON.stringify(this.bbAuth));
      }
      const params: PostUserContextRequestParams = { userContextPOST: { serviceAgreementId: this.serviceAgreementId } };
      this.userContextService
        .postUserContext(params)
        .pipe(take(1))
        .subscribe(
          (response) => {},
          () => {
            location.href = 'service-agreements';
          },
        );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.counter$?.unsubscribe();
  }
}
